/**
 * Profit Target Indicator Component
 *
 * Small, subtle dashboard indicator showing progress toward daily profit target.
 * - Green progress bar when below target
 * - Gold/amber when approaching (>80%)
 * - Green pill badge when target reached
 * - Hidden when disabled (limit = 0) or no data
 */

import React from 'react';
import { motion } from 'framer-motion';

interface ProfitTargetIndicatorProps {
  dailyProfitLimit: number;     // Dollar target (0 = disabled)
  globalDailyPnl: number;       // Current global P&L
  isTriggered: boolean;          // Whether circuit breaker is triggered
}

export function ProfitTargetIndicator({
  dailyProfitLimit,
  globalDailyPnl,
  isTriggered,
}: ProfitTargetIndicatorProps) {
  // Don't render if disabled
  if (dailyProfitLimit <= 0) return null;

  // Calculate progress
  const progressPct = Math.min(100, Math.max(0, (globalDailyPnl / dailyProfitLimit) * 100));
  const isProfitTriggered = isTriggered && globalDailyPnl >= dailyProfitLimit;
  const isApproaching = progressPct >= 80 && !isProfitTriggered;

  // Determine bar color
  const getBarColor = () => {
    if (isProfitTriggered) return 'bg-green-500';
    if (isApproaching) return 'bg-amber-500';
    if (globalDailyPnl >= 0) return 'bg-green-500';
    return 'bg-gray-400';
  };

  return (
    <motion.div
      className="mt-3 mb-1"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.4 }}
    >
      {/* Label */}
      <div className="flex items-center justify-between mb-1.5 text-xs text-gray-500 dark:text-gray-400">
        <span className="font-medium">
          {isProfitTriggered ? (
            <span className="text-green-600 dark:text-green-400 font-semibold">
              🎯 Profit Target Reached — Buys Paused
            </span>
          ) : (
            <span>🎯 Profit Target</span>
          )}
        </span>
        <span className={isProfitTriggered ? 'text-green-600 dark:text-green-400 font-semibold' : isApproaching ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}>
          ${globalDailyPnl >= 0 ? '' : '-'}{Math.abs(globalDailyPnl).toFixed(2)} / ${dailyProfitLimit.toLocaleString()}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="relative h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <motion.div
          className={`h-full rounded-full ${getBarColor()} ${
            isProfitTriggered ? 'shadow-sm shadow-green-500/30' : ''
          }`}
          initial={{ width: 0 }}
          animate={{ width: `${Math.max(0, progressPct)}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
      </div>
    </motion.div>
  );
}

